import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { Menu, X, Bell, LogOut } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUserContext } from "../../app/protectedRoute/UserContext";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { useNotifications } from "../../context/NotificationContext";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const { state, dispatch } = useUserContext();
  const { role, user } = state;
  const navigate = useNavigate();
  const { notifications, unreadCount, clearAll, markRead } = useNotifications();

  const isGuestLoggedIn = role === "guest";

  const handleConfirmLogout = () => {
    dispatch({ type: "RESET_USER" });
    localStorage.clear();
    navigate("/");
    setLogoutDialogOpen(false);
  };

  const notifBell = (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => { setNotifOpen(o => !o); markRead(); }}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>
      {notifOpen && (
        <div>
          <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span className="font-semibold text-sm text-gray-800">Notifications</span>
              {notifications.length > 0 && (
                <button className="text-xs text-primary hover:underline" onClick={clearAll}>
                  Clear all
                </button>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">No notifications yet</div>
              ) : (
                notifications.map(n => (
                  <div key={n.id} className="px-4 py-3 border-b border-gray-50 hover:bg-gray-50">
                    <p className="text-sm font-medium text-gray-800">{n.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{n.body}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{n.time.toLocaleTimeString()}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <motion.div
              className="flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
            >
              <Link to={isGuestLoggedIn ? "/guest-search-results" : "/"}>
                <span className="text-2xl md:text-3xl font-bold">
                  <span className="text-primary">Social</span>
                  <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md ml-1">Bunkr</span>
                </span>
              </Link>
            </motion.div>

            {isGuestLoggedIn ? (
              <>
                {/* Desktop — guest logged in */}
                <nav className="hidden md:flex items-center gap-6">
                  <Link to="/dashboard" className="text-foreground/80 hover:text-primary font-medium transition-colors">
                    My Bookings
                  </Link>
                  {notifBell}
                  <Button variant="ghost" className="text-muted-foreground hover:text-primary" onClick={() => setLogoutDialogOpen(true)}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </Button>
                  <Avatar className="h-9 w-9 border-2 border-primary">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {user?.first_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </nav>

                {/* Mobile — guest logged in */}
                <div className="flex items-center gap-2 md:hidden">
                  {notifBell}
                  <Button variant="ghost" size="icon" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                    {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Desktop — public */}
                <nav className="hidden md:flex items-center gap-8">
                  {["Home", "About", "Contact"].map((item) => (
                    <motion.div key={item} whileHover={{ y: -2 }}>
                      <Link
                        to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                        className="text-foreground/80 hover:text-primary font-medium transition-colors"
                      >
                        {item}
                      </Link>
                    </motion.div>
                  ))}
                </nav>
                <div className="hidden md:flex items-center gap-4">
                  <Link to="/login">
                    <Button variant="ghost" className="text-foreground hover:text-primary">Login</Button>
                  </Link>
                  <Link to="/signup">
                    <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6">Register</Button>
                  </Link>
                </div>

                {/* Mobile — public */}
                <button className="md:hidden p-2" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                  {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden py-4 border-t border-border"
            >
              {isGuestLoggedIn ? (
                <nav className="flex flex-col gap-3">
                  <Link to="/dashboard" className="text-foreground/80 hover:text-primary font-medium transition-colors px-2" onClick={() => setIsMenuOpen(false)}>
                    My Bookings
                  </Link>
                  <Button variant="ghost" className="justify-start text-muted-foreground hover:text-primary" onClick={() => { setIsMenuOpen(false); setLogoutDialogOpen(true); }}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </Button>
                </nav>
              ) : (
                <nav className="flex flex-col gap-4">
                  {["Home", "About", "Contact"].map((item) => (
                    <Link
                      key={item}
                      to={item === "Home" ? "/" : `/${item.toLowerCase()}`}
                      className="text-foreground/80 hover:text-primary font-medium transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item}
                    </Link>
                  ))}
                  <div className="flex flex-col gap-2 pt-4 border-t border-border">
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                      <Button variant="ghost" className="justify-start">Login</Button>
                    </Link>
                    <Link to="/signup" onClick={() => setIsMenuOpen(false)}>
                      <Button className="bg-primary text-primary-foreground">Register</Button>
                    </Link>
                  </div>
                </nav>
              )}
            </motion.div>
          )}
        </div>
      </motion.header>

      <Dialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Logout</DialogTitle>
            <DialogDescription>Are you sure you want to log out?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogoutDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirmLogout}>Logout</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Header;
